import { Component, computed, effect, inject, signal, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { Role, UserGroup } from 'depoto-core/src/entities'
import { historyBack } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { TranslateService } from '@ngx-translate/core'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { FormControl, FormGroup } from '@angular/forms'
import { TitleService } from '../../services'
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental'
import { UserGroupService } from '../../services/entity-services/userGroup.service'
import { UserRoleService } from '../../services/entity-services/userRole.service'
import { updateCachedListQueries } from '../../services/query-cache.service'

export const userGroupSchema = {
  id: null,
  name: null,
  roles: [],
  company: {
    id: null,
    name: null,
  },
}

@Component({
  selector: 'app-usergroup',
  templateUrl: './usergroup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class UserGroupComponent implements CheckDataChanges {
  private userGroupService = inject(UserGroupService)
  private userRoleService = inject(UserRoleService)
  private queryClient = injectQueryClient()

  // group: UserGroup
  error = false

  submitted = signal(false)
  private initialFormValue = signal<any>(null)

  userGroupForm = new FormGroup({
    name: new FormControl({ value: '', disabled: false }, []),
    roles: new FormControl({ value: [], disabled: false }, []),
  })

  readonly queryRoles = injectQuery(() => ({
    queryKey: ['roles'],
    queryFn: async () => {
      return await this.userRoleService
        .getRoles({}, { name: null, role: null })
        .then(roles => roles.map(role => new Role(role)))
    },
    onSuccess: data => {
      // do nothing ?
    },
  }))

  readonly queryUserGroup = injectQuery(() => ({
    queryKey: ['userGroup', Number(this.route.snapshot.params['id'])],
    queryFn: async () => {
      const id = this.route.snapshot.params['id']
      if (id) {
        return await this.userGroupService.getUserGroup(id, userGroupSchema)
      } else {
        return new UserGroup()
      }
    },
    onSuccess: data => {
      // Update title with group name
      this.titleService.updateTitleWithObject(data['id'], data['name'], 'title.group-id')
      this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      this.fillFormValue(data)
    },
  }))

  createUserGroup = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)
      if (this.userGroupForm.invalid) {
        throw new Error('Form is invalid')
      }
      if (this.userGroupForm.value.name.length < 3) {
        this.notificationService.warning(this.translateService.instant('user-group.length.warning'))
        return
      }
      const n = this.getDataToSave()

      // todo
      return this.userGroupService.createUserGroup(n, userGroupSchema)
    },
    onSuccess: data => {
      this.submitted.set(false)
      // After successful create, snapshot current form as the new baseline to avoid false unsaved prompts
      const current = this.userGroupForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.userGroupForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.userGroup.created'))
      // Update detail cache for this user group
      this.queryClient.setQueryData(['userGroup', Number(data.id)], data)
      // Update all cached list queries beginning with ['userGroups', ...] so listings reflect the change immediately
      updateCachedListQueries(this.queryClient, ['userGroups', 'options'], data, {
        mode: 'insert',
        insertPosition: 'start',
      })

      // LISTING ['userGroups', sortProp, sortDir]: On CREATE → refetch listing queries so ordering/pagination is correct
      this.removeUserGroupListings()

      this.router.navigate(['/group', data['id']], { replaceUrl: true })
    },
  }))

  updateUserGroup = injectMutation(() => ({
    mutationFn: () => {
      this.submitted.set(true)

      if (this.userGroupForm.invalid) {
        throw new Error('Form is invalid')
      }
      if (this.userGroupForm.value?.name?.length < 3) {
        this.notificationService.warning(this.translateService.instant('user-group.length.warning'))
        return
      }
      const userDataToSave = this.getDataToSave()
      return this.userGroupService.updateUserGroup(userDataToSave, userGroupSchema)
    },
    onSuccess: async data => {
      this.submitted.set(false)
      this.core.services.storage.getSync(this.core.services.storage.keys.auth.company)
      const id = Number(this.route.snapshot.params['id'])
      // Snapshot current form as baseline and mark pristine so guard won't prompt right after save
      const current = this.userGroupForm.getRawValue()
      this.initialFormValue.set(structuredClone(current))
      this.userGroupForm.markAsPristine()
      this.notificationService.success(this.translateService.instant('alert.usergroup.updated'))
      // Update detail cache for this user group
      this.queryClient.setQueryData(['userGroup', Number(data.id)], data)
      // Update all cached list queries beginning with ['userGroups', ...] so listings reflect the update immediately
      updateCachedListQueries(this.queryClient, ['userGroups', 'options'], data, { mode: 'update' })

      this.removeUserGroupListings()

      // this.router.navigate(['/user', data['id']])
    },
    // todo if error
    // this.notificationService.error(this.translateService.instant('alert.userGroup.error'))
  }))

  deleteUserGroup = injectMutation(() => ({
    mutationFn: async () => {
      const confirmation = confirm(
        this.translateService.instant('user-group.delete.confirmation', { name: this.userGroupForm.value.name })
      )
      if (confirmation) {
        this.submitted.set(true)
        const id = Number(this.route.snapshot.params['id'])
        await this.userGroupService.deleteUserGroup({ id }).then(() => {
          this.notificationService.success(this.translateService.instant('alert.usergroup.deleted'))
          const idStr = String(this.router.url.split('/')[2])
          // OPTIONS: keep as-is → update options cache by removing the userGroup
          updateCachedListQueries(this.queryClient, ['userGroups', 'options'], { id: Number(idStr) } as any, {
            mode: 'delete',
          })

          // LISTING ['userGroups', sortProp, sortDir]: On DELETE → if id is present in cached pages, remove it; otherwise do nothing
          this.removeFromListingsIfPresent(Number(idStr))
          // Optionally refresh listing pages
          this.router.navigate(['/userGroups'], { replaceUrl: true })
          historyBack()
        })

        // await this.core.services.userGroup.delete(this.group).then(() => {
      }
    },
    onSuccess: () => {
      // this.submitted.set(false)
      // // Prune deleted group from any cached lists (supports infinite queries and arrays)
      // const idStr = String(this.route.snapshot.params['id'])
      // updateCachedListQueries(this.queryClient, ['userGroups'], { id: Number(idStr) } as any, { mode: 'delete' })
      // // Remove detail cache for this user group
      // this.queryClient.removeQueries({ queryKey: ['userGroup', Number(idStr)], exact: true })
      // this.router.navigate(['/checkouts'], { replaceUrl: true })
    },
  }))

  isCreating = computed(() => {
    return !this.route.snapshot.params['id']
  })
  loading = computed(() => {
    return (
      this.queryUserGroup.isFetching() ||
      this.queryUserGroup.isPending() ||
      this.queryRoles.isFetching() ||
      this.queryRoles.isPending()
    )
  })

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private notificationService: NotificationService,
    private core: CoreService,
    private translateService: TranslateService,
    private titleService: TitleService
  ) {
    effect(() => {
      this.queryRoles.data()
      const d = this.queryUserGroup.data()
      const id = this.route.snapshot.params['id']
      if (id && d) {
        this.fillFormValue(d)
      }
    })
  }

  public fillFormValue(data) {
    const value = {
      name: data.name ?? '',
      roles: Array.isArray(data?.roles) ? data.roles : [],
    }

    this.userGroupForm.setValue(value)
    // Store initial values snapshot and reset form dirty state
    this.initialFormValue.set(structuredClone(value))
    this.userGroupForm.markAsPristine()
  }

  getDataToSave() {
    const current = this.userGroupForm.getRawValue() as any
    const initial = this.initialFormValue() as any
    const patch: any = {}

    // Include id when editing
    const routeId = this.route.snapshot.params['id']
    if (routeId) patch.id = Number(routeId)

    // Always include required fields
    patch.name = current.name

    // roles (array of strings) – include only when changed
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }
    if (!eqArrayStrings(current.roles, initial?.roles)) {
      patch.roles = current.roles || []
    }

    return patch
  }

  hasUnsavedDataChanges(): boolean {
    const current = this.userGroupForm.getRawValue() as any
    const initial = this.initialFormValue() as any

    if (!initial) {
      return this.userGroupForm.dirty
    }

    const norm = (v: any) => (v === undefined || v === null ? '' : String(v))
    const eqArrayStrings = (a: any[], b: any[]) => {
      const aa = Array.isArray(a) ? a.map(String).slice().sort() : []
      const bb = Array.isArray(b) ? b.map(String).slice().sort() : []
      if (aa.length !== bb.length) return false
      for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
      return true
    }

    return norm(current?.name) !== norm(initial?.name) || !eqArrayStrings(current?.roles, initial?.roles)
  }

  goBack(): void {
    historyBack()
  }

  updateRole(role: string, isEnabled: boolean) {
    if (isEnabled) {
      this.userGroupForm.value.roles.push(role)
    } else {
      this.userGroupForm.value.roles.forEach((r: string, i: number) => {
        if (r === role) {
          this.userGroupForm.value.roles.splice(i, 1)
        }
      })
    }
  }

  // ===== Helpers for cache handling of userGroup listings =====
  private isUserGroupsListingKey(key: unknown[]): boolean {
    if (!Array.isArray(key)) return false
    if (key.length !== 3) return false
    return key[0] === 'userGroups' && typeof key[1] === 'string' && typeof key[2] === 'string'
  }

  private listingContainsId(cached: any, id: number | string): boolean {
    const idStr = String(id)
    if (!cached || !Array.isArray(cached.pages)) return false
    for (const page of cached.pages) {
      const items = Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : undefined
      if (!Array.isArray(items)) {
        continue
      }
      if (items.some((it: any) => String(it?.id) === idStr)) {
        return true
      }
    }
    return false
  }

  private removeIdFromCachedListing(key: unknown[], id: number | string): void {
    const cached: any = this.queryClient.getQueryData(key as any)
    if (!cached || !Array.isArray(cached.pages)) return
    const idStr = String(id)
    const newPages = cached.pages.map((page: any) => {
      if (Array.isArray(page)) {
        return page.filter((it: any) => String(it?.id) !== idStr)
      }
      if (Array.isArray(page?.items)) {
        return { ...page, items: page.items.filter((it: any) => String(it?.id) !== idStr) }
      }
      return page
    })
    this.queryClient.setQueryData(key as any, { ...cached, pages: newPages })
  }

  // CREATE → refetch all cached userGroup listings (['userGroups', sortProp, sortDir])
  private removeUserGroupListings(): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['userGroups'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // UPDATE → refetch only listings that currently contain the userGroup id
  private refetchListingsContainingId(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['userGroups'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isUserGroupsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.queryClient.removeQueries({ queryKey: key as any, exact: true })
    }
  }

  // DELETE → if present in cached listings, remove it; otherwise do nothing
  private removeFromListingsIfPresent(id: number | string): void {
    const matches = this.queryClient.getQueriesData<any>({ queryKey: ['userGroups'] }) as Array<[unknown[], any]>
    for (const [key, cached] of matches) {
      if (!this.isUserGroupsListingKey(key as unknown[])) continue
      if (!this.listingContainsId(cached, id)) continue
      this.removeIdFromCachedListing(key as unknown[], id)
    }
  }
}
